import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Shield,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Mail,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";

// Set moment locale to French
moment.locale("fr");
import {
  fetchAdmins,
  createAdmin,
  updateAdmin,
  deleteAdmin,
  setSelectedAdminForEdit,
} from "@/store/slices/adminSlice";
import type {
  AdminUser,
  AdminCreateRequest,
  AdminUpdateRequest,
} from "@/services/adminService";

interface AdminsTabProps {
  admins: AdminUser[];
  adminsLoading: boolean;
  adminsError: string | null;
  currentAdminId?: string;
}

export const AdminsTab: React.FC<AdminsTabProps> = ({
  admins,
  adminsLoading,
  adminsError,
  currentAdminId,
}) => {
  const dispatch = useAppDispatch();
  const { selectedAdminForEdit } = useAppSelector((state) => state.admin);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<AdminUser | null>(null);

  // Form states
  const [createForm, setCreateForm] = useState<AdminCreateRequest>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  // Additional form fields for validation
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [editForm, setEditForm] = useState<AdminUpdateRequest>({
    firstName: "",
    lastName: "",
    email: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update edit form when selected admin changes
  useEffect(() => {
    if (selectedAdminForEdit) {
      setEditForm({
        firstName: selectedAdminForEdit.firstName,
        lastName: selectedAdminForEdit.lastName,
        email: selectedAdminForEdit.email,
      });
    }
  }, [selectedAdminForEdit]);

  const handleRefresh = () => {
    dispatch(fetchAdmins());
  };

  const handleCreateAdmin = async () => {
    if (
      !createForm.firstName ||
      !createForm.lastName ||
      !createForm.email ||
      !createForm.password ||
      !confirmEmail ||
      !confirmPassword
    ) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    if (createForm.email !== confirmEmail) {
      toast.error("Les adresses email ne correspondent pas");
      return;
    }

    if (createForm.password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    if (createForm.password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }

    try {
      setIsSubmitting(true);
      await dispatch(createAdmin(createForm)).unwrap();
      toast.success("Administrateur créé avec succès");
      setCreateDialogOpen(false);
      setCreateForm({ firstName: "", lastName: "", email: "", password: "" });
      setConfirmEmail("");
      setConfirmPassword("");
    } catch (error: any) {
      toast.error(error.message || "Erreur lors de la création");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditAdmin = (admin: AdminUser) => {
    dispatch(setSelectedAdminForEdit(admin));
    setEditDialogOpen(true);
  };

  const handleUpdateAdmin = async () => {
    if (!selectedAdminForEdit) return;

    if (!editForm.firstName || !editForm.lastName || !editForm.email) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    try {
      setIsSubmitting(true);
      await dispatch(
        updateAdmin({
          id: selectedAdminForEdit.id,
          data: editForm,
        }),
      ).unwrap();
      toast.success("Administrateur mis à jour avec succès");
      setEditDialogOpen(false);
      dispatch(setSelectedAdminForEdit(null));
    } catch (error: any) {
      toast.error(error.message || "Erreur lors de la mise à jour");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAdmin = (admin: AdminUser) => {
    setAdminToDelete(admin);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteAdmin = async () => {
    if (!adminToDelete) return;

    try {
      setIsSubmitting(true);
      await dispatch(deleteAdmin(adminToDelete.id)).unwrap();
      toast.success("Administrateur supprimé avec succès");
      setDeleteDialogOpen(false);
      setAdminToDelete(null);
    } catch (error: any) {
      toast.error(error.message || "Erreur lors de la suppression");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Gestion des Administrateurs
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Gérez les comptes administrateurs du système
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={adminsLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw
              className={`h-4 w-4 ${adminsLoading ? "animate-spin" : ""}`}
            />
            Actualiser
          </Button>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Nouvel Administrateur
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Créer un nouvel administrateur</DialogTitle>
                <DialogDescription>
                  Ajoutez un nouveau compte administrateur au système
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="create-firstName">Prénom</Label>
                    <Input
                      id="create-firstName"
                      value={createForm.firstName}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          firstName: e.target.value,
                        })
                      }
                      placeholder="Jean"
                    />
                  </div>
                  <div>
                    <Label htmlFor="create-lastName">Nom</Label>
                    <Input
                      id="create-lastName"
                      value={createForm.lastName}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          lastName: e.target.value,
                        })
                      }
                      placeholder="Dupont"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="create-email">Email</Label>
                  <Input
                    id="create-email"
                    type="email"
                    value={createForm.email}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, email: e.target.value })
                    }
                    placeholder="jean.dupont@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="create-confirm-email">
                    Confirmer l'email
                  </Label>
                  <Input
                    id="create-confirm-email"
                    type="email"
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    placeholder="jean.dupont@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="create-password">Mot de passe</Label>
                  <Input
                    id="create-password"
                    type="password"
                    value={createForm.password}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, password: e.target.value })
                    }
                    placeholder="••••••••"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum 8 caractères
                  </p>
                </div>
                <div>
                  <Label htmlFor="create-confirm-password">
                    Confirmer le mot de passe
                  </Label>
                  <Input
                    id="create-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setCreateDialogOpen(false);
                    setCreateForm({
                      firstName: "",
                      lastName: "",
                      email: "",
                      password: "",
                    });
                    setConfirmEmail("");
                    setConfirmPassword("");
                  }}
                  disabled={isSubmitting}
                >
                  Annuler
                </Button>
                <Button onClick={handleCreateAdmin} disabled={isSubmitting}>
                  {isSubmitting ? "Création..." : "Créer"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Admins Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Liste des Administrateurs
          </CardTitle>
        </CardHeader>
        <CardContent>
          {adminsLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : adminsError ? (
            <div className="text-center py-8">
              <p className="text-red-600 dark:text-red-400">
                Erreur: {adminsError}
              </p>
              <Button
                variant="outline"
                onClick={handleRefresh}
                className="mt-4"
              >
                Réessayer
              </Button>
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                Aucun administrateur trouvé
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Date de création</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow key={admin.id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center">
                            <span className="text-white text-sm font-medium">
                              {admin.firstName?.charAt(0).toUpperCase()}
                              {admin.lastName?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {admin.firstName} {admin.lastName}
                            </p>
                            {admin.id === currentAdminId && (
                              <Badge variant="outline" className="text-xs">
                                Vous
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Mail className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600 dark:text-gray-300">
                            {admin.email}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 dark:text-gray-300">
                          {moment(admin.createdAt).format("DD/MM/YYYY à HH:mm")}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                        >
                          Actif
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleEditAdmin(admin)}
                              className="flex items-center space-x-2"
                            >
                              <Edit className="h-4 w-4" />
                              <span>Modifier</span>
                            </DropdownMenuItem>
                            {admin.id !== currentAdminId && (
                              <DropdownMenuItem
                                onClick={() => handleDeleteAdmin(admin)}
                                className="flex items-center space-x-2 text-red-600"
                              >
                                <Trash2 className="h-4 w-4" />
                                <span>Supprimer</span>
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier l'administrateur</DialogTitle>
            <DialogDescription>
              Modifiez les informations de l'administrateur
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-firstName">Prénom</Label>
                <Input
                  id="edit-firstName"
                  value={editForm.firstName || ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, firstName: e.target.value })
                  }
                  placeholder="Jean"
                />
              </div>
              <div>
                <Label htmlFor="edit-lastName">Nom</Label>
                <Input
                  id="edit-lastName"
                  value={editForm.lastName || ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, lastName: e.target.value })
                  }
                  placeholder="Dupont"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, email: e.target.value })
                }
                placeholder="jean.dupont@example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditDialogOpen(false);
                dispatch(setSelectedAdminForEdit(null));
              }}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button onClick={handleUpdateAdmin} disabled={isSubmitting}>
              {isSubmitting ? "Mise à jour..." : "Mettre à jour"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer l'administrateur{" "}
              <strong>
                {adminToDelete?.firstName} {adminToDelete?.lastName}
              </strong>{" "}
              ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setAdminToDelete(null);
              }}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteAdmin}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Suppression..." : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
